// The table: one row per opportunity, every column sortable, each row opening the drawer.
//
// Every row is the same height. Each cell holds at most two single lines that end in "…" rather
// than wrap; the full text is on hover and in the drawer. Gong's suggested next step is a
// marker that opens a card on hover or focus, never text in the row, and a rep's next step that
// is cut short opens the same card. A leadership next step leads its cell, flagged as leadership's.
//
// At phone widths the same markup lays out as one card per row (see board.css); each cell's
// `data-label` supplies the label the hidden header row would have.

import { useEffect, useRef, useState } from 'react';
import type { FocusEvent, KeyboardEvent, MouseEvent, ReactNode } from 'react';
import type { Channel, LeadershipNextStep, Opportunity } from '../api/types.ts';
import type { TokenGetter } from '../api/client.ts';
import {
  formatAge, formatDate, formatDollars, formatLongDate, hasAmount, shortOpportunityName, splitName, textLines,
  withoutLeadingDate,
} from '../board/format.ts';
import { accountUrl, opportunityUrl } from '../board/salesforce.ts';
import { HOME_TAB_TAGS, funnelStageNumber, homeTab, showsHomeTab, touchLevel } from '../board/view.ts';
import type { Level, Sort, SortKey, Tab } from '../board/view.ts';
import { LeadershipNextStepSection } from './LeadershipNextStep.tsx';
import '../styles/leadership-next-step.css';

export const CHANNEL_LABELS: Record<Channel, string> = {
  email: 'Email',
  linkedin: 'LinkedIn',
  pylon: 'Pylon',
  call: 'Call',
  meeting: 'Meeting',
};

// Shape as well as colour, so a flag reads without colour and in print.
export const LEVEL_ICONS: Record<Level, string> = { good: '●', warn: '▲', bad: '■' };

const COLUMNS: Array<{ key: SortKey; label: string; numeric?: boolean; className: string }> = [
  { key: 'account', label: 'Account', className: 'col-account' },
  { key: 'opportunity', label: 'Opportunity', className: 'col-opportunity' },
  { key: 'rep', label: 'Rep', className: 'col-rep' },
  { key: 'stage', label: 'Stage', className: 'col-stage' },
  // Year 1 ARR, else qualified pipeline, else the amount: the payload's pipeline_value (GRO-246).
  { key: 'pipeline', label: 'Pipeline', numeric: true, className: 'col-pipeline' },
  { key: 'close', label: 'Close', className: 'col-close' },
  { key: 'last_touch', label: 'Last touch', className: 'col-touch' },
  { key: 'next_step', label: 'Next step', className: 'col-next' },
];

// One colour per stage, shared by the pill and the bubble so both views read the same (board.css).
// An open deal takes its stage's tone; a won or lost deal keeps the closed look.
function stageToneClass(opportunity: Opportunity): string {
  if (opportunity.status !== 'open') return `status-${opportunity.status}`;
  return `stage-tone-${funnelStageNumber(opportunity) ?? 'other'}`;
}

// The stage as a numbered, colour-coded bubble, shown only while the drawer sits beside the table
// (board.css). A stage outside 1-5 gets a neutral dash; the full name is on hover.
function StageBubble({ opportunity }: { opportunity: Opportunity }) {
  const stageNumber = funnelStageNumber(opportunity);
  const label = `${opportunity.stage}, ${opportunity.days_in_stage}d in stage`;
  return (
    <span className={`stage-bubble ${stageToneClass(opportunity)}`} title={label} aria-label={label}>
      {stageNumber ?? '–'}
    </span>
  );
}

// Clicks on a link, a rep or a marker inside a row do their own thing, not open the drawer.
function stopRowClick(event: MouseEvent | KeyboardEvent) {
  event.stopPropagation();
}

function ariaSort(sort: Sort | null, key: SortKey): 'ascending' | 'descending' | 'none' {
  if (!sort || sort.key !== key) return 'none';
  return sort.direction === 'asc' ? 'ascending' : 'descending';
}

function SortHeader({
  column, sort, onSort,
}: {
  column: (typeof COLUMNS)[number];
  sort: Sort | null;
  onSort: (key: SortKey) => void;
}) {
  const state = ariaSort(sort, column.key);
  const arrow = state === 'ascending' ? '↑' : state === 'descending' ? '↓' : '';
  return (
    <th aria-sort={state} className={column.className + (column.numeric ? ' numeric' : '')} scope="col">
      <button type="button" className="sort-button" onClick={() => onSort(column.key)}>
        {column.label}
        <span className="sort-arrow" aria-hidden="true">{arrow}</span>
      </button>
    </th>
  );
}

// The header row is hidden at phone widths, so the same sorts are offered as one select there.
function sortOptionValue(sort: Sort | null): string {
  return sort ? `${sort.key}:${sort.direction}` : 'default';
}

function parseSortOption(value: string): Sort | null {
  const [key, direction] = value.split(':');
  const column = COLUMNS.find(item => item.key === key);
  if (!column || (direction !== 'asc' && direction !== 'desc')) return null;
  return { key: column.key, direction };
}

function PhoneSort({ sort, onSortChange }: { sort: Sort | null; onSortChange: (sort: Sort | null) => void }) {
  return (
    <label className="phone-sort">
      <span className="menu-label">Sort</span>
      <select value={sortOptionValue(sort)} onChange={event => onSortChange(parseSortOption(event.target.value))}>
        <option value="default">Close date (default)</option>
        {COLUMNS.map(column => (
          <optgroup key={column.key} label={column.label}>
            <option value={`${column.key}:asc`}>{column.label}, ascending</option>
            <option value={`${column.key}:desc`}>{column.label}, descending</option>
          </optgroup>
        ))}
      </select>
    </label>
  );
}

// Whole dollars with the digits tabular and the commas not: this face's tabular comma is as wide
// as a digit, which opens a gap in "$420,000". Every comma is the same width, so right-aligned
// amounts still line up digit for digit.
export function Dollars({ amount }: { amount: number }) {
  const groups = formatDollars(amount).split(',');
  return (
    <span className="num">
      {groups.map((group, index) => (
        <span key={index}>{index > 0 ? <span className="comma">,</span> : null}{group}</span>
      ))}
    </span>
  );
}

// One line of a cell: never wraps, ends in "…", and shows its whole text on hover.
function Line({ children, title, className }: { children: ReactNode; title?: string; className?: string }) {
  return <span className={'line' + (className ? ` ${className}` : '')} title={title}>{children}</span>;
}

// A record name, linked to Salesforce when the payload says where it is.
function RecordName({ href, children }: { href: string | null; children: ReactNode }) {
  if (!href) return <>{children}</>;
  return (
    <a className="record-link" href={href} target="_blank" rel="noreferrer" onClick={stopRowClick}>{children}</a>
  );
}

// What a touch's colour means, in words, for its card.
const LEVEL_MEANINGS: Record<Level, string> = {
  good: 'Touched by the rep in the last 7 days.',
  warn: 'Getting old (8 to 13 days), or touched only by someone other than the rep.',
  bad: 'Stale: no email, call, meeting or message from us in 14 days or more.',
};

function LastTouchCell({ opportunity, today }: { opportunity: Opportunity; today: string }) {
  const touch = opportunity.last_touch;
  const level = touchLevel(opportunity, today);
  const icon = level ? <span className="level-icon" aria-hidden="true">{LEVEL_ICONS[level]}</span> : null;

  if (!touch) {
    return (
      <HoverCard
        align="left"
        triggerClassName="line-group"
        label="Never touched"
        triggerContent={<Line className={level ? `level-${level}` : 'muted'}>{icon}Never touched</Line>}
      >
        <span className="popover-head">Last touch from our side</span>
        <span className="popover-body">No email, call, meeting or message from us is on record for this account.</span>
      </HoverCard>
    );
  }

  // Who made it, only when it wasn't the rep; the card says the rest.
  const byline = touch.by_owner ? null : touch.by ? touch.by.name : 'Someone other than the rep';
  const repLast = opportunity.last_owner_touch_date;
  const detail = [byline, touch.subject].filter(Boolean).join(' · ');
  // `by_owner` means the row's rep made it (GRO-245; the payload keeps the old name), so the name
  // is the toucher's own, never the opportunity owner, who is an inactive user on most renewals.
  const byWhom = touch.by_owner
    ? `the rep, ${touch.by?.name ?? 'unnamed'}`
    : (touch.by?.name ?? 'someone other than the rep (the source names nobody)');
  return (
    <HoverCard
      align="left"
      triggerClassName="line-group"
      label={`Last touch ${formatAge(touch.date, today)}`}
      triggerContent={(
        <>
          <Line className={level ? `level-${level}` : undefined}>
            {icon}
            <span className="touch-age num">{formatAge(touch.date, today)}</span>
            <span className="touch-channel">{CHANNEL_LABELS[touch.channel]}</span>
          </Line>
          <Line className="sub">{detail}</Line>
        </>
      )}
    >
      <span className="popover-head">Last touch from our side</span>
      <dl className="card-facts">
        <dt>When</dt><dd>{formatLongDate(touch.date)} ({formatAge(touch.date, today)})</dd>
        <dt>How</dt><dd>{CHANNEL_LABELS[touch.channel]}</dd>
        <dt>By</dt><dd>{byWhom}</dd>
        {touch.subject ? <><dt>Subject</dt><dd>{touch.subject}</dd></> : null}
        {touch.by_owner ? null : (
          <><dt>Rep's own</dt><dd>{repLast ? `${formatLongDate(repLast)} (${formatAge(repLast, today)})` : 'None on record'}</dd></>
        )}
      </dl>
      {level ? <span className={`card-meaning level-${level}`}>{icon}{LEVEL_MEANINGS[level]}</span> : null}
    </HoverCard>
  );
}

// The opportunity's full name and its type, behind the shortened name in the row.
function OpportunityCell({ opportunity, instanceUrl }: { opportunity: Opportunity; instanceUrl: string | null }) {
  const shortName = shortOpportunityName(opportunity.opp_name, opportunity.account_name);
  const kind = [opportunity.type, opportunity.record_type].filter(Boolean).join(' · ');
  return (
    <HoverCard
      align="left"
      triggerClassName="line"
      label={opportunity.opp_name}
      triggerContent={<RecordName href={opportunityUrl(instanceUrl, opportunity.opp_id)}>{shortName}</RecordName>}
    >
      <span className="popover-head">Opportunity</span>
      <dl className="card-facts">
        <dt>Name</dt><dd>{opportunity.opp_name}</dd>
        <dt>Type</dt><dd>{kind || 'Not set'}</dd>
      </dl>
    </HoverCard>
  );
}

// A card that opens on hover or focus and stays open while the pointer is on its trigger or on
// the card itself, so it can be read and scrolled. Clicks inside it never open the drawer.
//
// Snappy, and one at a time:
//   - opening waits a moment, so sweeping the pointer across the table doesn't flash cards;
//   - once a card is open, the next opens at once and closes the last, so two are never up;
//   - leaving waits a moment too, long enough to cross from the trigger onto the card;
//   - a card whose leadership editor has focus stays open when the pointer leaves, and other cards
//     don't open over it. Saving or cancelling once the pointer has gone closes it, and so does
//     focus leaving the card from anywhere in it (clicking or tabbing away).
const OPEN_DELAY_MS = 90;
const CLOSE_DELAY_MS = 120;

// The open card's close function. A module variable rather than state, so opening a card
// re-renders that card and the one it closes, not every card in the table.
let closeOpenCard: (() => void) | null = null;

function HoverCard({
  align = 'right', triggerClassName, triggerContent, label, children,
}: {
  // Which edge of the trigger the card lines up with; left for the columns left of centre.
  align?: 'left' | 'right';
  triggerClassName: string;
  triggerContent: ReactNode;
  label: string;
  children: ReactNode;
}) {
  const [open, setOpen] = useState(false);
  const wrapper = useRef<HTMLSpanElement>(null);
  const openTimer = useRef<number | null>(null);
  const closeTimer = useRef<number | null>(null);
  const closeSelf = useRef(() => setOpen(false));
  // The pointer left while the editor was in use, so the card closes once the editor is done.
  const leftWhileEditing = useRef(false);

  const cancelTimers = () => {
    if (openTimer.current !== null) window.clearTimeout(openTimer.current);
    if (closeTimer.current !== null) window.clearTimeout(closeTimer.current);
    openTimer.current = null;
    closeTimer.current = null;
  };

  // Open this card, closing whichever other one is open.
  const show = () => {
    cancelTimers();
    if (closeOpenCard && closeOpenCard !== closeSelf.current) closeOpenCard();
    closeOpenCard = closeSelf.current;
    setOpen(true);
  };
  const hide = () => {
    cancelTimers();
    leftWhileEditing.current = false;
    if (closeOpenCard === closeSelf.current) closeOpenCard = null;
    setOpen(false);
  };
  useEffect(() => hide, []);

  // Focus in an open leadership editor means someone is typing; a click on a card's text doesn't count.
  const editingHere = () => Boolean(wrapper.current?.querySelector('.leadership-step-editor')?.contains(document.activeElement));
  const anotherCardInUse = () => {
    const active = document.activeElement;
    return Boolean(active?.closest('.popover .leadership-step-editor')) && !wrapper.current?.contains(active);
  };

  const onEnter = () => {
    cancelTimers();
    leftWhileEditing.current = false;
    if (anotherCardInUse()) return;
    if (closeOpenCard) show();
    else openTimer.current = window.setTimeout(show, OPEN_DELAY_MS);
  };
  const onLeave = () => {
    cancelTimers();
    if (editingHere()) {
      leftWhileEditing.current = true;
      return;
    }
    closeTimer.current = window.setTimeout(hide, CLOSE_DELAY_MS);
  };

  // On the wrapper, so focus leaving from inside the card closes it too, not only from the trigger.
  const onBlur = (event: FocusEvent) => {
    if (!wrapper.current?.contains(event.relatedTarget as Node | null)) hide();
  };
  // The editor hands focus back to its Edit button when it closes; with the pointer gone, so does the card.
  const onFocusInside = () => {
    if (leftWhileEditing.current && !editingHere()) hide();
  };

  return (
    <span
      ref={wrapper}
      className="hovercard"
      onMouseEnter={onEnter}
      onMouseLeave={onLeave}
      onFocus={onFocusInside}
      onBlur={onBlur}
    >
      <span className={triggerClassName} tabIndex={0} aria-label={label} onFocus={show}>
        {triggerContent}
      </span>
      {open ? (
        <span className={`popover popover-${align}`} role="tooltip" onClick={stopRowClick}>
          <span className="popover-card" tabIndex={-1}>{children}</span>
        </span>
      ) : null}
    </span>
  );
}

// The lines of a card: a list when there are several, else one paragraph.
function CardLines({ lines }: { lines: string[] }) {
  if (lines.length > 1) return <ul className="card-list">{lines.map((line, index) => <li key={index}>{line}</li>)}</ul>;
  return <>{lines[0] ?? ''}</>;
}

// The rep's next step, a leadership next step and Gong's suggestion in one card. Neither Gong's
// reading of a call nor leadership's step counts as the rep's next step, so a row with only those
// is still red.
function NextStepCell({
  opportunity, today, getToken, onLeadershipSaved,
}: {
  opportunity: Opportunity;
  today: string;
  getToken: TokenGetter;
  onLeadershipSaved: (oppId: string, value: LeadershipNextStep | null) => void;
}) {
  const nextStep = opportunity.next_step;
  const gong = opportunity.gong_next_step;
  const leadership = opportunity.leadership_next_step;
  const isOpen = opportunity.status === 'open';
  const hasRepStep = nextStep.source === 'rep' && Boolean(nextStep.text);
  const onlyGong = isOpen && !hasRepStep && gong !== null;

  // The rep's step without the date they typed in front of it; that date is line two.
  const lines = hasRepStep
    ? textLines(nextStep.text ?? '').map((line, index) => (index === 0 ? withoutLeadingDate(line) : line))
    : [];
  const text = lines.join(' ');

  // The rep's step as a row line, or the gap where it should be.
  const repLine = hasRepStep ? (
    <Line>{text}</Line>
  ) : isOpen ? (
    <Line className="level-bad"><span className="level-icon" aria-hidden="true">{LEVEL_ICONS.bad}</span>No next step</Line>
  ) : (
    <Line className="muted">—</Line>
  );
  const gongMarker = gong ? (
    <span className={'gong-marker' + (onlyGong ? ' level-warn' : '')}>
      {onlyGong ? <span className="level-icon" aria-hidden="true">{LEVEL_ICONS.warn}</span> : null}
      Gong · {formatDate(gong.call_date, today)}
    </span>
  ) : null;

  // With a leadership step the cell keeps its two lines: leadership's step leads, flagged, and the
  // rep's drops to line two in short. The Noted date is then only in the card.
  const firstLine = leadership ? (
    <Line><span className="leadership-flag">Leadership</span>{leadership.text}</Line>
  ) : repLine;
  const secondLine = leadership ? (
    <span className="line sub line-with-marker">
      {hasRepStep ? (
        <span className="marker-line-text">Rep: {text}</span>
      ) : isOpen ? (
        <span className="marker-line-text level-bad">
          <span className="level-icon" aria-hidden="true">{LEVEL_ICONS.bad}</span>No rep next step
        </span>
      ) : null}
      {gongMarker}
    </span>
  ) : (
    <span className="line sub line-with-marker">
      {hasRepStep && nextStep.date ? <span>Noted {formatDate(nextStep.date, today)}</span> : null}
      {gongMarker}
    </span>
  );

  const repLabel = hasRepStep ? `Next step: ${text}` : 'No next step';
  // What a missing rep step stands despite, named in the card's last line.
  const despite = [leadership ? 'the leadership next step' : null, gong ? "Gong's suggestion" : null]
    .filter(Boolean).join(' and ');

  return (
    <HoverCard
      triggerClassName="line-group"
      label={leadership ? `Leadership next step: ${leadership.text}. ${repLabel}` : repLabel}
      triggerContent={<>{firstLine}{secondLine}</>}
    >
      {/* Leadership's step, with the editor for leadership and admin. Without a step it shows
          only to them, as an Add. */}
      <LeadershipNextStepSection
        opportunity={opportunity}
        today={today}
        getToken={getToken}
        onSaved={onLeadershipSaved}
        variant="card"
      />

      <span className="popover-head card-section-after-leadership">{leadership ? "Rep's next step" : 'Next step'}</span>
      <dl className="card-facts">
        <dt>Step</dt><dd>{hasRepStep ? <CardLines lines={lines} /> : 'None entered'}</dd>
        {hasRepStep ? (
          <><dt>Noted</dt><dd>{nextStep.date ? `${formatLongDate(nextStep.date)} (${formatAge(nextStep.date, today)})` : 'No date'}</dd></>
        ) : null}
        <dt>Source</dt><dd>{hasRepStep ? 'The rep, in Salesforce' : "The rep's Next Step field in Salesforce is empty"}</dd>
      </dl>

      {gong ? (
        <>
          <span className="popover-head card-section">Gong suggestion</span>
          <dl className="card-facts">
            <dt>From</dt><dd>The call on {formatLongDate(gong.call_date)} ({formatAge(gong.call_date, today)})</dd>
            <dt>Suggests</dt><dd><CardLines lines={textLines(gong.text)} /></dd>
          </dl>
        </>
      ) : null}

      {isOpen && !hasRepStep ? (
        <span className="card-meaning level-bad">
          <span className="level-icon" aria-hidden="true">{LEVEL_ICONS.bad}</span>
          No next step: this counts toward needing attention{despite ? `, even with ${despite}` : ''}.
        </span>
      ) : gong ? (
        <span className="card-meaning muted">Gong's reading of the call doesn't count as the rep's next step.</span>
      ) : null}
    </HoverCard>
  );
}

function Row({
  opportunity, today, instanceUrl, currentTab, selected, getToken, onOpen, onRep, onLeadershipSaved,
}: {
  opportunity: Opportunity;
  today: string;
  instanceUrl: string | null;
  currentTab: Tab;
  selected: boolean;
  getToken: TokenGetter;
  onOpen: (opportunity: Opportunity) => void;
  onRep: (ownerId: string) => void;
  onLeadershipSaved: (oppId: string, value: LeadershipNextStep | null) => void;
}) {
  const accountName = opportunity.account_name;
  const pastClose = opportunity.attention.past_close;
  // The rep, never the opportunity owner: most renewals are owned by an inactive user.
  const [firstName, lastName] = splitName(opportunity.rep.name);

  // On All: a tag naming the tab the row lives on.
  const home = homeTab(opportunity);
  const homeTag = home !== null && showsHomeTab(currentTab) ? HOME_TAB_TAGS[home] : null;

  // The row is a keyboard target too: Enter or Space opens it, as a click does.
  const onKeyDown = (event: KeyboardEvent<HTMLTableRowElement>) => {
    if (event.target !== event.currentTarget) return;
    if (event.key !== 'Enter' && event.key !== ' ') return;
    event.preventDefault();
    onOpen(opportunity);
  };
  const onRepClick = (event: MouseEvent) => {
    stopRowClick(event);
    onRep(opportunity.rep.user_id);
  };

  return (
    <tr
      tabIndex={0}
      className={selected ? 'is-selected' : undefined}
      onClick={() => onOpen(opportunity)}
      onKeyDown={onKeyDown}
      aria-label={`${accountName ?? 'No account'}: ${opportunity.opp_name}${homeTag ? ` (${homeTag})` : ''}`}
    >
      <td data-label="Account" className="cell-account">
        <Line title={accountName ?? undefined} className={accountName ? undefined : 'muted'}>
          <RecordName href={accountName ? accountUrl(instanceUrl, opportunity.account_id) : null}>
            {accountName ?? 'No account'}
          </RecordName>
        </Line>
        {homeTag ? (
          <span className="line sub"><span className={`home-tag home-tag-${home}`}>{homeTag}</span></span>
        ) : null}
      </td>
      <td data-label="Opportunity" className="cell-opportunity">
        <OpportunityCell opportunity={opportunity} instanceUrl={instanceUrl} />
      </td>
      <td data-label="Rep" className="cell-rep">
        <button type="button" className="rep-button" onClick={onRepClick} onKeyDown={stopRowClick} title={`Show only ${opportunity.rep.name}`}>
          <span className="line">{firstName}</span>
          <span className="line">{lastName}</span>
        </button>
      </td>
      <td data-label="Stage" className="cell-stage">
        <span className={`stage-pill ${stageToneClass(opportunity)}`} title={opportunity.stage}>{opportunity.stage}</span>
        <Line className="sub num">{opportunity.days_in_stage}d in stage</Line>
        {/* The compact form the side-by-side drawer shows instead: the stage's number in a bubble. */}
        <StageBubble opportunity={opportunity} />
      </td>
      <td data-label="Pipeline" className="numeric cell-pipeline num">
        {hasAmount(opportunity.pipeline_value)
          ? <Dollars amount={opportunity.pipeline_value} />
          : <span className="muted" title="No Year 1 ARR, qualified pipeline or amount in Salesforce">—</span>}
      </td>
      <td data-label="Close" className="cell-close">
        <Line className={'num' + (pastClose ? ' level-bad' : '')} title={formatLongDate(opportunity.close_date)}>
          {formatDate(opportunity.close_date, today)}
        </Line>
        {pastClose ? (
          <Line className="sub level-bad"><span className="level-icon" aria-hidden="true">{LEVEL_ICONS.bad}</span>Past close</Line>
        ) : null}
      </td>
      <td data-label="Last touch" className="cell-touch">
        <LastTouchCell opportunity={opportunity} today={today} />
      </td>
      <td data-label="Next step" className="cell-next">
        <NextStepCell opportunity={opportunity} today={today} getToken={getToken} onLeadershipSaved={onLeadershipSaved} />
      </td>
    </tr>
  );
}

export function PipelineTable({
  rows, today, instanceUrl, sort, currentTab, selectedId, empty, getToken, onSort, onSortChange, onOpen, onRep,
  onLeadershipSaved,
}: {
  rows: Opportunity[];
  today: string;
  instanceUrl: string | null;
  sort: Sort | null;
  // The tab picked, so a row on All can say which tab it lives on.
  currentTab: Tab;
  selectedId: string | null;
  empty: ReactNode;
  getToken: TokenGetter;
  onSort: (key: SortKey) => void;
  onSortChange: (sort: Sort | null) => void;
  onOpen: (opportunity: Opportunity) => void;
  onRep: (ownerId: string) => void;
  onLeadershipSaved: (oppId: string, value: LeadershipNextStep | null) => void;
}) {
  return (
    <div className="table-box">
      <PhoneSort sort={sort} onSortChange={onSortChange} />
      <table className="pipeline">
        <colgroup>
          {COLUMNS.map(column => <col key={column.key} className={column.className} />)}
        </colgroup>
        <thead>
          <tr>
            {COLUMNS.map(column => (
              <SortHeader key={column.key} column={column} sort={sort} onSort={onSort} />
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 ? (
            <tr className="empty-row"><td colSpan={COLUMNS.length}>{empty}</td></tr>
          ) : rows.map(opportunity => (
            <Row
              key={opportunity.opp_id}
              opportunity={opportunity}
              today={today}
              instanceUrl={instanceUrl}
              currentTab={currentTab}
              selected={opportunity.opp_id === selectedId}
              getToken={getToken}
              onOpen={onOpen}
              onRep={onRep}
              onLeadershipSaved={onLeadershipSaved}
            />
          ))}
        </tbody>
      </table>
    </div>
  );
}
